import { useState } from 'react';

import { errorText as textOf, useStrings } from '../i18n/index.ts';
import { addWithChecks } from '../state/cart-sync.ts';
import { ageVerdict, setGuestBirthDate, setMirroredBirthDate, type AgeGateRequest } from '../state/gates.ts';
import { isSignedIn, setBirthDate } from '../state/session.ts';
import { Button, ErrorBanner, FieldBox, GlassButton, ScreenTitle } from '../ui/kit.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import '../styles/cart.css';

/** Дата по местному календарю как `YYYY-MM-DD`. */
function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function defaultBirthDate(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 25);
  return isoDay(date);
}

/**
 * Подтверждение возраста: дата рождения уходит в профиль, товар 18+ ложится в корзину, если
 * покупателю есть 18. Младше — товар не добавляется, дата остаётся, чтобы не спрашивать снова.
 */
export function AgeGateSheet({ request, onClose }: { request: AgeGateRequest; onClose: () => void }) {
  const strings = useStrings();
  const [birthDate, setDate] = useState(defaultBirthDate);
  const [isSaving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const today = isoDay(new Date());

  const confirm = async (close: () => void) => {
    if (!birthDate || birthDate > today) return;
    setSaving(true);
    setErrorText(null);
    try {
      if (isSignedIn()) {
        try {
          await setBirthDate(birthDate);
        } catch (error) {
          setErrorText(textOf(strings, error));
          return;
        }
      } else {
        // Аккаунта ещё нет: дата остаётся в браузере и уйдёт в профиль при входе.
        setGuestBirthDate(birthDate);
      }
      setMirroredBirthDate(birthDate);
      if (ageVerdict() !== 'adult') {
        setErrorText(strings.ageGateUnderage);
        return;
      }
      if (request.item) addWithChecks(request.item);
      close();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet size="auto" label={strings.ageGateTitle} onClose={onClose}>
      {(close) => (
        <>
          <div className="sheet-header">
            <GlassButton icon="close" label={strings.cancel} onClick={close} />
          </div>
          <form
            className="sheet-body sheet-pad stack readable age-gate"
            onSubmit={(event) => {
              event.preventDefault();
              void confirm(close);
            }}
          >
            <ScreenTitle text={strings.ageGateTitle} />
            <p className="t-subheadline t-muted">{strings.ageGateText}</p>
            <FieldBox label={strings.birthDate}>
              <input type="date" required max={today} value={birthDate} autoComplete="bday" onChange={(event) => setDate(event.target.value)} />
            </FieldBox>
            {errorText && <ErrorBanner text={errorText} />}
            <Button type="submit" busy={isSaving}>
              {strings.ageGateConfirm}
            </Button>
          </form>
        </>
      )}
    </Sheet>
  );
}
